import { createReducer } from 'typesafe-actions';
import { createSelector } from 'reselect';
import { ConfigActions } from '@store/actions';
import { fetchConfig } from '@store/actions/config.actions';
import { RootState } from '@store';
import { get, isEmpty } from 'lodash';

export type IConfigResponse = {
    success: boolean;
    apiName: string;
    version: string;
    correlationId: string;
    timestamp: string;
    responseDetails: IConfigResponseDetails;
};
export type IConfigResponseDetails = {
    analyticsEvents: string[];
    validations: Validations;
    customerServiceHours: CustomerServiceHours;
    isFeedbackAllowed: boolean;
    feedbackAllowedOS: string[];
    isOriginationLoansAllowed: boolean;
    appStoreUrls: AppStoreUrls;
    isRefi: string;
    isPurchase: string;
    features: Features;
    durationsInDays: DurationInDays;
    messageCenterPortCodes: number[];
};

export type DurationInDays = {
    recheckPaperlessEnrollment: number;
    recheckTCPAEnrollment: number;
    recheckMobileAppDownload: number;
};

export type AppStoreUrls = {
    android: string;
    ios: string;
};

export type CustomerServiceHours = {
    monThuStartHour: string;
    monThuEndHour: string;
    friStartHour: string;
    friEndHour: string;
    satStartHour: string;
    satEndHour: string;
};

export type Features = {
    fullApp: FullApp;
    lipV2: LipV2;
    lipRefresh: boolean;
    lipNtb: boolean;
    onboarding: boolean;
    onboardingChangeMobileNumber: boolean;
    onboardingMobileAppDownload: boolean;
    onboardingPaperless: boolean;
    onboardingTCPA: boolean;
    onboardingWelcome: boolean;
    nextBestPopup: boolean;
    acpForAllStatus: boolean;
};

export type LipV2 = {
    enable: boolean;
};

export type FullApp = {
    rt: Fha;
    fha: Fha;
    va: Fha;
};

export type Fha = {
    enable: boolean;
};

export type Validations = {
    password: Password;
};

export type Password = {
    firstName: boolean;
    lastName: boolean;
    userName: boolean;
};

export interface IConfigState {
    response?: IConfigResponseDetails;
}

const initialState: IConfigState = {};

export const config = createReducer<IConfigState, ConfigActions>(initialState)
    .handleAction(fetchConfig.success, (state, action) => {
        return { ...state, ...action.payload };
    })
    .handleAction(fetchConfig.failure, (state, _) => ({
        ...state,
        response: undefined
    }));

const configSlice = (state: RootState) => state.config;
const configResponse = createSelector(configSlice, slice => slice);

export const getFullAppFeatureFlags = createSelector(configResponse, (slice: IConfigResponseDetails) => {
    console.log({ slice });
    if (!slice) return false;

    const { features } = slice;

    return Object.entries(features).reduce((result: any, [key, value]) => {
        const [fullApp, fullAppKey] = key.split('.');
        if (fullApp === 'fullApp') {
            Object.assign(result, { [fullAppKey]: value });
        }
        return result;
    }, {});
});

export const getOnboardingAppFeatureFlags = createSelector(configResponse, (slice: IConfigResponseDetails) => {
    if (!slice) return false;

    const { features } = slice;

    return Object.entries(features).reduce((result1: any, [key, value]) => {
        if (key.indexOf('onboarding') > -1) {
            Object.assign(result1, { [key]: value });
        }
        return result1;
    }, {});
});

export const getDurationsInDays = createSelector(configResponse, (slice: IConfigResponseDetails) => {
    if (!slice) return false;
    const { durationsInDays } = slice;
    return durationsInDays;
});
export const selectAllowedPortcodesForMessageCentre = createSelector(configResponse, (slice: IConfigResponseDetails): number[] => {
    return get(slice, ['messageCenterPortCodes'], []);
});
export const getLipV2FeatureFlag = createSelector(configResponse, (slice: IConfigResponseDetails) => {
    if (!slice) return false;
    const {
        features: { lipV2 }
    } = slice;
    return lipV2;
});

export const getLipNTBFeatureFlag = createSelector(configResponse, (slice: IConfigResponseDetails) => {
    if (!slice) return false;
    const {
        features: { lipNtb }
    } = slice;
    return lipNtb;
});

export const getFullAppFeatureFlagsV2 = createSelector(configSlice, (slice: any) => {
    console.log({ slice });
    if (!slice) return false;

    const features = get(slice, ['features'], undefined);

    if (!features) return undefined;
    return Object.entries(features).reduce((result: any, [key, value]) => {
        const [fullApp, fullAppKey] = key.split('.');
        if (fullApp === 'fullApp') {
            Object.assign(result, { [fullAppKey]: value });
        }
        return result;
    }, {});
});
export const getNBPFeatureFlag = createSelector(configSlice, (slice: any) => {
    if (!slice) return false;
    const features = get(slice, ['features'], undefined);
    if (!features) return false;
    return features?.nextBestPopup;
});

export const selectFlag = (_: RootState, keyword: string) => keyword;
export const selectFeatureFlag = createSelector([configSlice, selectFlag], (slice: IConfigState, keyword: string) => {
    if (isEmpty(slice)) return false;
    return get(slice, ['features', keyword], false);
});
